import type { TrainingFrameContext } from './runtime';

export const COLORS = {
  ink: '#09140f',
  pitch: '#103e2b',
  pitchDeep: '#071f17',
  pitchLine: 'rgba(232, 239, 226, 0.56)',
  paper: '#f1f2e8',
  muted: '#aebcb1',
  lime: '#c8ff43',
  cyan: '#72d4c5',
  coral: '#ff735c',
  opponent: '#ff7c66',
} as const;

export function clearCanvas(context: CanvasRenderingContext2D, frame: TrainingFrameContext): void {
  context.save();
  context.setTransform(frame.devicePixelRatio, 0, 0, frame.devicePixelRatio, 0, 0);
  const gradient = context.createLinearGradient(0, 0, 0, frame.height);
  gradient.addColorStop(0, COLORS.pitchDeep);
  gradient.addColorStop(1, COLORS.ink);
  context.fillStyle = gradient;
  context.fillRect(0, 0, frame.width, frame.height);
  context.restore();
}

export function drawGrid(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  spacing = 48,
  opacity = 0.08,
): void {
  context.save();
  context.strokeStyle = `rgba(200, 255, 67, ${opacity})`;
  context.lineWidth = 1;
  context.beginPath();
  for (let x = spacing / 2; x < width; x += spacing) {
    context.moveTo(x, 0);
    context.lineTo(x, height);
  }
  for (let y = spacing / 2; y < height; y += spacing) {
    context.moveTo(0, y);
    context.lineTo(width, y);
  }
  context.stroke();
  context.restore();
}

export function drawCenteredText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  options: {
    size?: number;
    color?: string;
    weight?: number;
    family?: string;
    align?: CanvasTextAlign;
  } = {},
): void {
  context.save();
  context.fillStyle = options.color ?? COLORS.paper;
  context.font = `${options.weight ?? 600} ${options.size ?? 20}px ${options.family ?? 'system-ui, sans-serif'}`;
  context.textAlign = options.align ?? 'center';
  context.textBaseline = 'middle';
  context.fillText(text, x, y);
  context.restore();
}

export function drawTriangle(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  fill: string,
  rotation = 0,
): void {
  context.save();
  context.translate(x, y);
  context.rotate(rotation);
  context.beginPath();
  context.moveTo(0, -radius);
  context.lineTo(radius * 0.9, radius * 0.75);
  context.lineTo(-radius * 0.9, radius * 0.75);
  context.closePath();
  context.fillStyle = fill;
  context.fill();
  context.restore();
}

export function pingPong(value: number, length = 1): number {
  const cycle = ((value % (length * 2)) + length * 2) % (length * 2);
  return cycle <= length ? cycle : length * 2 - cycle;
}

export function easeInOut(value: number): number {
  const clamped = Math.max(0, Math.min(1, value));
  return clamped * clamped * (3 - 2 * clamped);
}

